import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import LinkButton from '@/gf/components/LinkButtonOld'
import Modal from '@/gf/components/Modal'
import StoreOrderEvents from '@/gf/components/StoreOrderEvents'
import TextArea from '@/gf/components/TextArea'
import useConfig from '@/gf/hooks/useConfig'
import useMsgs from '@/gf/hooks/useMsgs'
import { TIMELINE_QUERY } from '@/gf/hooks/useTimelineEvents'
import useToggle from '@/gf/hooks/useToggle'
import RfqEventM from '@/gf/modules/RfqEvent'
import StoreOrderEventM from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import UserM from '@/gf/modules/User'
import RESEND_CONFIRMATION_EMAIL from '@/gf/mutations/orders'
import useSession from '@/suppliers/hooks/useSession'
import * as api from '@/suppliers/modules/api'
import type { RfqEvent, StoreOrderEvent } from '@/types'
import { gql, useMutation } from '@apollo/client'
import React from 'react'
import { StoreOrder } from './useFetchStoreOrder'

const TimelineCard = ({
  storeOrder,
  reloadStoreOrder,
  storeOrderEvents,
  rfqEvents,
  openMessagesTab,
}: {
  storeOrder: StoreOrder
  reloadStoreOrder: () => Promise<unknown>
  storeOrderEvents?: StoreOrderEvent[]
  rfqEvents?: RfqEvent[]
  openMessagesTab: () => void
}) => {
  const config = useConfig()
  const [_, msgr] = useMsgs()
  const { user } = useSession()
  const [editEmailAdditionalCommentsText, setEditEmailAdditionalCommentsText] = React.useState('')
  const [commentText, setCommentText] = React.useState('')
  const [resendEditedEmailModal, resendEditedEmailModalToggler] = useToggle()
  const [confirmModalOpen, confirmModalToggler] = useToggle()
  const [resendConfirmationEmail] = useMutation(RESEND_CONFIRMATION_EMAIL)
  const [spinnerLive, spinnerToggler] = useToggle()

  const [addStoreOrderTimelineNote] = useMutation(
    gql`
      mutation AddStoreOrderTimelineNote($storeOrderId: String, $note: String, $privacy: Privacy) {
        addStoreOrderTimelineNote(storeOrderId: $storeOrderId, note: $note, privacy: $privacy)
      }
    `,
    { refetchQueries: [TIMELINE_QUERY] }
  )

  const resendEmail = async () => {
    await resendConfirmationEmail({
      variables: { storeOrderId: storeOrder.id },
    })

    msgr.add('Confirmation email sent', 'positive')
    reloadStoreOrder()
    confirmModalToggler.off()
  }

  const addTimelineNote = (
    privacy: 'SUPPLIER_PROTECTED' | 'PUBLIC' | 'BUYER_PROTECTED' | 'PRIVATE'
  ) => {
    spinnerToggler.on()
    addStoreOrderTimelineNote({
      variables: {
        storeOrderId: storeOrder.id,
        note: commentText,
        privacy,
      },
    })
      .then(() => {
        msgr.add('Added Comment', 'positive')
        setCommentText('')
        reloadStoreOrder()
      })
      .catch(() => {
        msgr.add('Error adding comment', 'negative')
      })
      .finally(() => {
        spinnerToggler.off()
      })
  }

  const events = storeOrderEvents
    ?.map(
      StoreOrderEventM.getSupplierEventDisplayData(
        user,
        confirmModalToggler.on,
        (storeOrderEvent: StoreOrderEvent) => {
          resendEditedEmailModalToggler.on()
          setEditEmailAdditionalCommentsText(storeOrderEvent.note)
        }
      )
    )
    .concat(
      rfqEvents ? rfqEvents.map(RfqEventM.getEventDisplayData(UserM.isAdmin(user), config)) : []
    )
    .sort((a, b) => Time.sortDesc(a.datetime, b.datetime))

  return (
    <>
      <Modal
        open={resendEditedEmailModal}
        onClose={resendEditedEmailModalToggler.off}
        title="Resend Order Quote Email"
        footerAction={
          <Button
            title="Send"
            onClick={() =>
              api
                .sendOrderEditEmail(storeOrder.id, editEmailAdditionalCommentsText)
                .then((response) => {
                  if (response.status === 204) {
                    msgr.add('Email sent', 'positive')
                    setEditEmailAdditionalCommentsText('')
                  } else {
                    msgr.add('Whoops, something went wrong. Please contact support.', 'negative')
                  }
                  reloadStoreOrder()
                  resendEditedEmailModalToggler.off()
                })
            }
          />
        }
      >
        <p>Resend an email to the customer notifying them of an order quote.</p>
        <p className="mt-4">Add additional comments for customer (optional)</p>
        <p>ATTENTION: The customer will see these comments.</p>
        <TextArea
          className="mt-2"
          value={editEmailAdditionalCommentsText}
          onChange={(e) => setEditEmailAdditionalCommentsText(e.target.value)}
        />
      </Modal>

      <Modal
        open={confirmModalOpen}
        onClose={confirmModalToggler.off}
        title="Resend Email"
        footerAction={<Button title="Send" onClick={resendEmail} />}
      >
        <p>Resend confirmation email to the customer notifying them of the order.</p>
      </Modal>

      <Card title="Timeline">
        <Card.Section>
          <Card.SubSection>
            <form className="space-y-2">
              <TextArea
                placeholder="Leave a comment..."
                required
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <div className="flex justify-between items-start">
                <Button
                  title="Post"
                  disabled={!commentText || spinnerLive}
                  performing={spinnerLive}
                  onClick={() =>
                    addTimelineNote(UserM.isAdmin(user) ? 'PRIVATE' : 'SUPPLIER_PROTECTED')
                  }
                />
                <div className="text-sm text-gray-500 space-x-2 space-y-0.5 text-right">
                  <p>Only you and your team can see comments</p>
                  <p>
                    To contact the customer or Gearflow, go to the{' '}
                    <LinkButton onClick={openMessagesTab}>messages tab</LinkButton>
                  </p>
                </div>
              </div>
            </form>
          </Card.SubSection>
          <Card.SubSection>
            <StoreOrderEvents events={events} state={storeOrder.state} />
          </Card.SubSection>
        </Card.Section>
      </Card>
    </>
  )
}

export default TimelineCard
